/**
 * PII detection — isomorphic (Node + browser). Pure regex/JS, no Node-only
 * dependencies, so it can be imported by backend services AND the web-client.
 *
 * Used by:
 *  - the Audit Service, to catalogue PII findings at record time
 *    (see `audit_pii_findings`), and
 *  - the web-client, to highlight potential PII in the audit detail view.
 *
 * This is a *detector*, not a redactor. It flags "potential PII" — patterns are
 * deliberately tuned to favour catching real PII over silence, and the UI
 * always labels findings as *potential*. Secret/credential scrubbing is a
 * separate concern (see `redact.ts`).
 */

export type PIIType =
  | 'email'
  | 'phone'
  | 'ssn'
  | 'credit_card'
  | 'ip_address'
  | 'iban'
  | 'passport';

export interface PIIFinding {
  type: PIIType;
  /** Start offset into the scanned string (inclusive). */
  start: number;
  /** End offset into the scanned string (exclusive). */
  end: number;
  /** Masked sample of the matched value — NEVER the raw PII. */
  sample: string;
}

export interface PIIScanResult {
  findings: PIIFinding[];
  /** Number of findings per type — every PIIType is present (0 when none). */
  counts: Record<PIIType, number>;
}

export interface DetectPIIOptions {
  /** PII types to scan for. Defaults to {@link DEFAULT_PII_TYPES}. */
  include?: readonly PIIType[];
}

export const ALL_PII_TYPES: readonly PIIType[] = [
  'email',
  'phone',
  'ssn',
  'credit_card',
  'ip_address',
  'iban',
  'passport',
];

/**
 * On by default: the employee / customer identity and financial data that
 * organizations are most sensitive about.
 *
 * `ip_address` and `passport` are opt-in (pass them via `options.include`):
 *  - Cade is a cybersecurity assistant — tool output is saturated with IP
 *    addresses, so highlighting every one would drown the real signal.
 *  - the passport pattern is intentionally loose and false-positive prone.
 */
export const DEFAULT_PII_TYPES: readonly PIIType[] = [
  'email',
  'phone',
  'ssn',
  'credit_card',
  'iban',
];

/** Hard ceiling on scanned characters — keeps a pathologically large input cheap. */
const MAX_SCAN_CHARS = 256 * 1024;

// --- validators -------------------------------------------------------------

/** Luhn (mod-10) check — the primary defence against credit-card false positives. */
function luhnValid(digits: string): boolean {
  if (digits.length === 0) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/** ISO 13616 mod-97 check for IBANs. */
function ibanValid(raw: string): boolean {
  const compact = raw.replace(/\s/g, '').toUpperCase();
  if (compact.length < 15 || compact.length > 34) return false;
  // Move the 4-char country/check prefix to the end, then mod-97 must equal 1.
  const rearranged = compact.slice(4) + compact.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    let chunk: string;
    if (ch >= 'A' && ch <= 'Z') {
      chunk = String(ch.charCodeAt(0) - 55); // A=10 .. Z=35
    } else if (ch >= '0' && ch <= '9') {
      chunk = ch;
    } else {
      return false;
    }
    for (let i = 0; i < chunk.length; i++) {
      remainder = (remainder * 10 + (chunk.charCodeAt(i) - 48)) % 97;
    }
  }
  return remainder === 1;
}

/** Validate a candidate phone string: 10-15 digits (excludes dates / short ids). */
function phoneValid(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

/** Validate a dashed SSN, rejecting structurally impossible groups. */
function ssnValid(raw: string): boolean {
  const parts = raw.split('-');
  if (parts.length !== 3) return false;
  const area = parts[0] ?? '';
  const group = parts[1] ?? '';
  const serial = parts[2] ?? '';
  if (area === '000' || area === '666') return false;
  if (Number(area) >= 900) return false;
  if (group === '00') return false;
  if (serial === '0000') return false;
  return true;
}

/** Validate a candidate credit-card string (length + Luhn). */
function cardValid(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 13 && digits.length <= 19 && luhnValid(digits);
}

/** Validate an IPv4 dotted quad (each octet 0-255); IPv6 candidates pass through. */
function ipValid(raw: string): boolean {
  if (raw.includes(':')) return true; // IPv6 — pattern-matched only
  const octets = raw.split('.');
  if (octets.length !== 4) return false;
  return octets.every((o) => {
    if (o.length === 0 || o.length > 3) return false;
    const n = Number(o);
    return Number.isInteger(n) && n >= 0 && n <= 255;
  });
}

// --- masks (output is NEVER the raw value) ----------------------------------

function maskEmail(raw: string): string {
  const at = raw.indexOf('@');
  if (at < 1) return '***';
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const tld = dot >= 0 ? domain.slice(dot) : '';
  return `${local.charAt(0)}***@${domain.charAt(0)}***${tld}`;
}

function lastDigits(raw: string, keep: number): string {
  return raw.replace(/\D/g, '').slice(-keep);
}

function maskPhone(raw: string): string {
  return `***${lastDigits(raw, 4)}`;
}

function maskSsn(raw: string): string {
  return `***-**-${lastDigits(raw, 4)}`;
}

function maskCard(raw: string): string {
  return `**** **** **** ${lastDigits(raw, 4)}`;
}

function maskIp(raw: string): string {
  if (raw.includes(':')) {
    const head = raw.split(':')[0] ?? '';
    return `${head}:****`;
  }
  const o = raw.split('.');
  return `${o[0] ?? ''}.${o[1] ?? ''}.*.*`;
}

function maskIban(raw: string): string {
  const compact = raw.replace(/\s/g, '');
  const stars = '*'.repeat(Math.max(0, compact.length - 8));
  return `${compact.slice(0, 4)}${stars}${compact.slice(-4)}`;
}

function maskGeneric(raw: string): string {
  if (raw.length <= 3) return '***';
  return `${raw.charAt(0)}***${raw.slice(-2)}`;
}

// --- detector registry ------------------------------------------------------

interface Detector {
  type: PIIType;
  /** Higher specificity wins when two matches overlap. */
  specificity: number;
  /** Factory — returns a fresh global regex so `lastIndex` is never shared. */
  pattern: () => RegExp;
  /** Optional post-match validation; a match that fails is discarded. */
  validate?: (raw: string) => boolean;
  /** Produce a masked sample for a matched value. */
  mask: (raw: string) => string;
}

const DETECTORS: readonly Detector[] = [
  {
    type: 'email',
    specificity: 9,
    pattern: () => /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
    mask: maskEmail,
  },
  {
    type: 'credit_card',
    specificity: 8,
    // `(?<![\w.])`/`(?![\w.])` reject a run that is preceded or followed by a
    // word char OR a dot — so the fractional part of a decimal (e.g. a
    // 0.881097… similarity score) is never mistaken for a card number.
    pattern: () => /(?<![\w.])(?:\d[ -]?){12,18}\d(?![\w.])/g,
    validate: cardValid,
    mask: maskCard,
  },
  {
    type: 'iban',
    specificity: 8,
    pattern: () => /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/g,
    validate: ibanValid,
    mask: maskIban,
  },
  {
    type: 'ssn',
    specificity: 7,
    pattern: () => /\b\d{3}-\d{2}-\d{4}\b/g,
    validate: ssnValid,
    mask: maskSsn,
  },
  {
    type: 'ip_address',
    specificity: 6,
    pattern: () =>
      /\b(?:\d{1,3}\.){3}\d{1,3}\b|\b(?:[A-Fa-f0-9]{1,4}:){2,7}[A-Fa-f0-9]{1,4}\b/g,
    validate: ipValid,
    mask: maskIp,
  },
  {
    type: 'phone',
    specificity: 3,
    // Optional +CC, optional (area), then >=2 digit groups joined by separators.
    // Digit-count validation (10-15) excludes dates and short identifiers.
    // The leading `(?<!\d)(?<!\d[\s.-])` and trailing `(?![\s.-]?\d)` boundaries
    // stop a phone matching a fragment of a longer number (e.g. a 16-digit card).
    pattern: () =>
      /(?<!\d)(?<!\d[\s.-])(?:\+\d{1,3}[\s.-]?)?(?:\(\d{1,4}\)[\s.-]?)?\d{1,4}[\s.-]\d{1,4}(?:[\s.-]\d{1,9})?(?![\s.-]?\d)/g,
    validate: phoneValid,
    mask: maskPhone,
  },
  {
    type: 'passport',
    specificity: 2,
    pattern: () => /\b[A-Z]{1,2}\d{6,9}\b/g,
    mask: maskGeneric,
  },
];

function emptyCounts(): Record<PIIType, number> {
  return {
    email: 0,
    phone: 0,
    ssn: 0,
    credit_card: 0,
    ip_address: 0,
    iban: 0,
    passport: 0,
  };
}

/**
 * Scan `text` for potential PII.
 *
 * Overlapping matches are resolved in favour of the more specific detector
 * (e.g. a dashed SSN is reported as `ssn`, not `phone`).
 */
export function detectPII(text: string, options?: DetectPIIOptions): PIIScanResult {
  const counts = emptyCounts();
  if (!text) return { findings: [], counts };

  const scanned = text.length > MAX_SCAN_CHARS ? text.slice(0, MAX_SCAN_CHARS) : text;
  const include = new Set(options?.include ?? DEFAULT_PII_TYPES);

  const candidates: Array<{ det: Detector; start: number; end: number; value: string }> = [];
  for (const det of DETECTORS) {
    if (!include.has(det.type)) continue;
    const re = det.pattern();
    let match: RegExpExecArray | null;
    while ((match = re.exec(scanned)) !== null) {
      const value = match[0];
      if (value.length === 0) {
        re.lastIndex++;
        continue;
      }
      if (det.validate && !det.validate(value)) continue;
      candidates.push({ det, start: match.index, end: match.index + value.length, value });
    }
  }

  // Resolve overlaps: highest specificity first, then earliest start.
  candidates.sort((a, b) => b.det.specificity - a.det.specificity || a.start - b.start);
  const kept: typeof candidates = [];
  for (const cand of candidates) {
    const overlaps = kept.some((k) => cand.start < k.end && k.start < cand.end);
    if (!overlaps) kept.push(cand);
  }
  kept.sort((a, b) => a.start - b.start);

  const findings: PIIFinding[] = kept.map((k) => {
    counts[k.det.type] += 1;
    return {
      type: k.det.type,
      start: k.start,
      end: k.end,
      sample: k.det.mask(k.value),
    };
  });

  return { findings, counts };
}
